import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  NAV_PATHS,
  NOTIFICATION_TIMEOUT_LONG_6S,
} from '@modules/common/constants';
import {
  useGetDefaultTenantTimeRange,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { useInitSearchStoreFromSearchParams } from '@modules/feed/hooks';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { searchInitialState } from '@modules/feed/store/searchInitialState';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { getSearchQueryFromMetadata } from '@modules/feed/utils';
import { Status } from 'generated/graphql';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AndOfErrors } from 'v2/core/errors/errors';
import useGetDashboard from 'v2/core/repository/edge-server/dashboard/useGetDashboard';
import { AndStatuses, IsLoading } from 'v2/core/repository/edge-server/types';
import useGetDashboardDeps from 'v2/core/services/dashboard/useGetDashboardDeps';
import useParseSavedSlugInfo from 'v2/core/services/savedSlug/useParseSavedSlugInfo';

export const useInitSearchStore = () => {
  const { showNotification, showErrorNotification } = useNotificationContext();
  const dispatch = useSearchStore(({ dispatch }) => dispatch);
  const { token, savedSlug: dashboardSlug } = useParseSavedSlugInfo();
  const { data: deps, status: dStatus, error: dError } = useGetDashboardDeps();
  const baseCondition = useSearchStore(({ query }) => query.baseCondition);
  const whereClause = useSearchStore(({ query }) => query.whereClause);
  const viewToken = useSearchStore(({ data: { viewToken } = {} }) => viewToken);
  const { duration } = useSearchStore(({ query: { duration } }) => ({
    duration,
  }));
  const URLParams = new URLSearchParams(document.location.search);
  const hasFromAndToClause = URLParams.has('from') && URLParams.has('to');
  const navigate = useNavigate();

  const {
    user: { id: userID },
    tenantID,
    timezone,
    sourceDetailsMap,
  } = deps ?? {};

  const {
    data: dashboard,
    status: gStatus,
    error: gError,
  } = useGetDashboard(
    {
      token: token || '',
      tenantID,
      userID,
      sourceDetailsMap,
      includeScheduleOutbounds: true,
      timezone,
    },
    {
      Skip: !token || !sourceDetailsMap,
      onCompleted: (data) => {
        if (data.getDashboardByToken) {
          dispatch({
            type: SearchActionType.SET_DATA,
            payload: {
              version: data.getDashboardByToken.version,
            },
          });
        }
      },
    },
  );

  const status = AndStatuses([dStatus, gStatus]);
  const error = AndOfErrors([dError, gError]);
  const isLoading = IsLoading(status);

  const defaultTimeRange = useGetDefaultTenantTimeRange();

  useEffect(() => {
    searchInitialState.query.duration = defaultTimeRange;
  }, [defaultTimeRange]);

  useEffect(() => {
    if (token) {
      dispatch({
        type: SearchActionType.RESET,
      });

      return () => {
        dispatch({
          type: SearchActionType.RESET,
        });
      };
    }
  }, [dispatch, token]);

  useInitSearchStoreFromSearchParams();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to fetch dashboard`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  useEffect(() => {
    if (dashboard?.status === Status.Archived) {
      dispatch({
        type: SearchActionType.RESET,
      });
      navigate(`/${isSearchToFeedEnabled ? NAV_PATHS.FEED : NAV_PATHS.SEARCH}`);
      showNotification({
        contentComponent: 'This Search is archived.',
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
          severity: 'info',
        },
      });
      return;
    }

    if (
      dashboard?.viewToken &&
      (!viewToken || viewToken !== dashboard?.viewToken)
    ) {
      dispatch({
        type: SearchActionType.SET_DATA,
        payload: dashboard,
      });

      dispatch({
        type: SearchActionType.SET_UPDATE_QUERY,
        payload: {
          duration,
          ...getSearchQueryFromMetadata({
            metadata: dashboard.metadata,
            getDurationField: !hasFromAndToClause,
            defaultTimeRange,
          }),
        },
      });
    }
  }, [
    baseCondition,
    duration,
    hasFromAndToClause,
    dashboard,
    dispatch,
    viewToken,
    dashboardSlug,
    showNotification,
    navigate,
    defaultTimeRange,
    isSearchToFeedEnabled,
  ]);

  const hasWhereClause = Boolean(whereClause);

  return {
    isLoading: Boolean(token) && isLoading,
    hasWhereClause: Boolean(hasWhereClause),
    error,
  };
};
